import { Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { getAuthFailureRedirectPath } from '../lib/authFailureRedirect';
import { useAuthStore, CLIENT_PORTAL_ROLES } from '../store/authStore';

export default function ProtectedRoute({
  children,
  allowedRoles = [],
  requireCertificateAccess = false,
}) {
  const { isAuthenticated, user, isTokenExpired, logout } = useAuthStore();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Allow bypass only during development.
  const devMode =
    import.meta.env.DEV &&
    (
      searchParams.get('dev') === '1' ||
      import.meta.env.VITE_DEV_BYPASS === '1'
    );

  if (devMode) {
    return children;
  }

  // Token expired - logout and redirect (aligned with API 401/403 handler)
  if (isAuthenticated && isTokenExpired?.()) {
    const userSnapshot = useAuthStore.getState().user;
    const path = getAuthFailureRedirectPath(userSnapshot);
    logout();
    return <Navigate to={path} replace />;
  }

  // Not authenticated - redirect to login (client routes → tenant portal logon when slug known)
  if (!isAuthenticated || !user) {
    if (location.pathname.startsWith('/client')) {
      try {
        const slug = sessionStorage.getItem('lastPortalSlug');
        if (slug) {
          return <Navigate to={`/${slug}/logon`} replace />;
        }
      } catch {
        /* ignore */
      }
    }
    return <Navigate to="/login" replace />;
  }

  // Check if user role is allowed (tenant client portal: `client` legacy JWT or `client_portal`)
  const effectiveAllowed =
    allowedRoles.length > 0 && allowedRoles.includes('client')
      ? Array.from(new Set([...allowedRoles.filter((r) => r !== 'client'), ...CLIENT_PORTAL_ROLES]))
      : allowedRoles;

  if (effectiveAllowed.length > 0 && !effectiveAllowed.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    switch (user.role) {
      case 'global_admin':
        return <Navigate to="/admin/dashboard" replace />;
      case 'tenant_admin':
      case 'tenant_user':
        return <Navigate to="/tenant/dashboard" replace />;
      case 'client':
      case 'client_portal':
        return <Navigate to="/client/dashboard" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  // Check certificate access permission if required
  if (requireCertificateAccess) {
    // Global admin and tenant admin always have access
    if (user.role === 'global_admin' || user.role === 'tenant_admin') {
      return children;
    }

    // For tenant_user, check the permission flag
    if (user.role === 'tenant_user') {
      const canAccess = user.canAccessCertificates !== undefined ? user.canAccessCertificates : true;
      if (!canAccess) {
        // Redirect to dashboard if access is denied
        return <Navigate to="/tenant/dashboard" replace />;
      }
    }
  }

  // User is authenticated and has correct role
  return children;
}

